import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useDebounce } from 'use-debounce';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import { ReactComponent as TaskIcon } from '../../../../../assets/images/task-icon.svg';
import './styles.scss';

// The forms placed on the Tasks page are picked from a second tree, opened right
// below the library one. A folder id alone cannot say which tree a level belongs
// to -- the two number their folders apart -- so every request names it.
const TASKS_TREE = 'tasks';

// The tasks tree lists the tasks themselves after its forms; they are picked like the
// forms are, and the report takes both. A task id is no form id, so the two are kept apart.
const isTaskRow = (item) => item.type === 'task';

const flattenBreadcrumbs = (breadcrumbsNode) => {
  const result = [];
  let current = breadcrumbsNode;
  while (current) {
    result.push({ id: current.id, name: current.name, tree: current.tree ?? null });
    current = current.children;
  }
  return result;
};

const FormsModal = ({
  onClose,
  onConfirmFormsSelection,
  fetchForms,
  fetchFolderFormIds,
  previousSelectedForms = [],
  previousSelectedFormsData = [],
  previousSelectedTasks = [],
}) => {
  const [search, setSearch] = useState('');
  // taskId -> taskName of the tasks picked.
  const [selectedTasks, setSelectedTasks] = useState(() => new Map(previousSelectedTasks.map((t) => [Number(t.id), t.name])));
  const [selectedFormIds, setSelectedFormIds] = useState(() => new Set(previousSelectedForms.map((id) => Number(id))));
  const [selectedFormsData, setSelectedFormsData] = useState(() => {
    const map = new Map();
    previousSelectedFormsData.forEach((f) => map.set(Number(f.id), f.name));
    return map;
  });
  const [debouncedSearch] = useDebounce(search, 300);

  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  // Cache: folderId -> { forms: Map<formId, formName>, tasks: Map<taskId, taskName> } for
  // everything inside that folder (tasks only in a folder of the Tasks page).
  const folderFormsCache = useRef(new Map());
  const folderFormsRequests = useRef(new Map());

  // Track which folders are currently loading their form IDs
  const [loadingFolderIds, setLoadingFolderIds] = useState(new Set());

  const isSearchMode = debouncedSearch.trim().length > 0;

  const getFolderContents = useCallback(
    async (folderId, folderTree = null) => {
      // Keyed by tree as well: the same id is a different folder in each.
      const cacheKey = `${folderTree ?? 'library'}:${folderId}`;

      if (folderFormsCache.current.has(cacheKey)) {
        return folderFormsCache.current.get(cacheKey);
      }
      if (folderFormsRequests.current.has(cacheKey)) {
        return folderFormsRequests.current.get(cacheKey);
      }

      const request = fetchFolderFormIds(folderId, folderTree)
        .then((response) => {
          if (isResponseOk(response)) {
            const contents = {
              forms: new Map((response.data.forms || []).map((f) => [Number(f.id), f.name])),
              tasks: new Map((response.data.tasks || []).map((t) => [Number(t.id), t.name])),
            };
            folderFormsCache.current.set(cacheKey, contents);
            return contents;
          }

          return { forms: new Map(), tasks: new Map() };
        })
        .finally(() => {
          folderFormsRequests.current.delete(cacheKey);
        });

      folderFormsRequests.current.set(cacheKey, request);
      return request;
    },
    [fetchFolderFormIds],
  );

  const loadFolder = useCallback(
    async (folderId, folderTree = null) => {
      setIsLoading(true);
      try {
        const response = await fetchForms({ folderId, tree: folderTree });
        if (isResponseOk(response)) {
          const data = response.data;
          setItems(data.data || []);
          if (data.breadcrumbs) {
            setBreadcrumbs(flattenBreadcrumbs(data.breadcrumbs));
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fetchForms],
  );

  const loadSearch = useCallback(
    async (query) => {
      setIsLoading(true);
      try {
        const response = await fetchForms({ search: query });
        if (isResponseOk(response)) {
          const data = response.data;
          setSearchResults(Array.isArray(data) ? data : data.data || []);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fetchForms],
  );

  useEffect(() => {
    loadFolder(null);
  }, [loadFolder]);

  useEffect(() => {
    if (isSearchMode) {
      loadSearch(debouncedSearch.trim());
    } else {
      setSearchResults(null);
    }
  }, [debouncedSearch, isSearchMode, loadSearch]);

  const folders = useMemo(() => items.filter((item) => item.type === 'folder'), [items]);
  const levelItems = useMemo(() => items.filter((item) => item.type !== 'folder'), [items]);

  // The forms and tasks on show: the level's, or the matches of a search.
  const displayItems = useMemo(() => (isSearchMode ? searchResults || [] : levelItems), [isSearchMode, searchResults, levelItems]);
  const displayForms = useMemo(() => displayItems.filter((item) => !isTaskRow(item)), [displayItems]);
  const taskRows = useMemo(() => displayItems.filter(isTaskRow), [displayItems]);

  // The two trees are listed apart, the Tasks page below the library under its own
  // separator. Inside the tasks tree every row belongs to it, so the separator stays
  // and the library half is simply empty.
  const isFromTasks = (item) => item.tree === TASKS_TREE;
  const libraryFolders = useMemo(() => folders.filter((folder) => !isFromTasks(folder)), [folders]);
  const tasksFolders = useMemo(() => folders.filter(isFromTasks), [folders]);
  const libraryItems = useMemo(() => displayForms.filter((item) => !isFromTasks(item)), [displayForms]);
  const tasksItems = useMemo(() => displayForms.filter(isFromTasks), [displayForms]);
  const hasTasksItems = tasksFolders.length > 0 || taskRows.length > 0 || tasksItems.length > 0;

  // Pre-fetch folder form IDs for visible folders so we can determine checkbox state
  const [folderStates, setFolderStates] = useState({});

  const allVisibleFormIds = useMemo(() => displayForms.map((f) => Number(f.id)), [displayForms]);
  const allVisibleTaskIds = useMemo(() => taskRows.map((t) => Number(t.id)), [taskRows]);
  const hasVisibleSelectableFolders = useMemo(
    () => !isSearchMode && folders.some((folder) => folderStates[folder.id] !== null),
    [folderStates, folders, isSearchMode],
  );
  const hasVisibleSelectableItems = allVisibleFormIds.length > 0 || allVisibleTaskIds.length > 0 || hasVisibleSelectableFolders;
  const isAllVisibleItemsSelected =
    hasVisibleSelectableItems &&
    allVisibleFormIds.every((id) => selectedFormIds.has(id)) &&
    allVisibleTaskIds.every((id) => selectedTasks.has(id)) &&
    (!hasVisibleSelectableFolders || folders.every((folder) => folderStates[folder.id] === true || folderStates[folder.id] === null));

  useEffect(() => {
    if (isSearchMode || !folders.length) {
      setFolderStates({});
      return;
    }

    let cancelled = false;

    const computeStates = async () => {
      const states = {};
      await Promise.all(
        folders.map(async (folder) => {
          const { forms: formsMap, tasks: tasksMap } = await getFolderContents(folder.id, folder.tree ?? null);
          if (cancelled) return;
          if (formsMap.size === 0 && tasksMap.size === 0) {
            states[folder.id] = null;
            return;
          }
          states[folder.id] =
            [...formsMap.keys()].every((id) => selectedFormIds.has(id)) && [...tasksMap.keys()].every((id) => selectedTasks.has(id));
        }),
      );
      if (!cancelled) {
        setFolderStates(states);
      }
    };

    computeStates();

    return () => {
      cancelled = true;
    };
  }, [folders, selectedFormIds, selectedTasks, isSearchMode, getFolderContents]);

  const handleNavigateToFolder = (folder) => {
    loadFolder(folder.id, folder.tree ?? null);
  };

  const handleBreadcrumbClick = (crumb, index) => {
    // The outermost crumb is the library root the picker opens on -- the level the
    // tasks tree is shown at, so walking back to it leaves that tree behind.
    if (index === 0) {
      loadFolder(null, null);
      return;
    }

    loadFolder(crumb.id, crumb.tree ?? null);
  };

  const setFoldersLoadingState = useCallback((folderIds, shouldLoad) => {
    if (!folderIds.length) {
      return;
    }

    setLoadingFolderIds((prev) => {
      const next = new Set(prev);

      folderIds.forEach((folderId) => {
        if (shouldLoad) {
          next.add(folderId);
        } else {
          next.delete(folderId);
        }
      });

      return next;
    });
  }, []);

  const applyTasksSelection = useCallback((tasksMap, shouldSelect) => {
    if (!tasksMap.size) {
      return;
    }

    setSelectedTasks((prev) => {
      const next = new Map(prev);

      tasksMap.forEach((name, id) => {
        if (shouldSelect) {
          next.set(id, name);
        } else {
          next.delete(id);
        }
      });

      return next;
    });
  }, []);

  const applyFormsSelection = useCallback((formsMap, shouldSelect) => {
    if (!formsMap.size) {
      return;
    }

    setSelectedFormIds((prev) => {
      const next = new Set(prev);

      formsMap.forEach((_, id) => {
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });

      return next;
    });

    setSelectedFormsData((prev) => {
      const next = new Map(prev);

      if (shouldSelect) {
        formsMap.forEach((name, id) => {
          next.set(id, name);
        });
      } else {
        formsMap.forEach((_, id) => {
          next.delete(id);
        });
      }

      return next;
    });
  }, []);

  const handleToggleFolder = async (folder) => {
    const folderId = folder.id;
    setFoldersLoadingState([folderId], true);

    try {
      const { forms: formsMap, tasks: tasksMap } = await getFolderContents(folderId, folder.tree ?? null);
      const shouldSelect = folderStates[folderId] !== true;

      applyFormsSelection(formsMap, shouldSelect);
      applyTasksSelection(tasksMap, shouldSelect);
    } finally {
      setFoldersLoadingState([folderId], false);
    }
  };

  // When we load folder contents, update names for any forms that are in selectedFormsData with placeholder names
  useEffect(() => {
    if (!items.length) return;
    const formItems = items.filter((item) => item.type === 'form');
    if (!formItems.length) return;

    setSelectedFormsData((prev) => {
      let changed = false;
      const next = new Map(prev);
      formItems.forEach((form) => {
        const id = Number(form.id);
        if (next.has(id) && next.get(id) !== form.name) {
          next.set(id, form.name);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [items]);

  // Also update names from search results
  useEffect(() => {
    if (!searchResults?.length) return;

    setSelectedFormsData((prev) => {
      let changed = false;
      const next = new Map(prev);
      searchResults
        .filter((item) => !isTaskRow(item))
        .forEach((form) => {
          const id = Number(form.id);
          if (next.has(id) && next.get(id) !== form.name) {
            next.set(id, form.name);
            changed = true;
          }
        });
      return changed ? next : prev;
    });
  }, [searchResults]);

  const handleSelectForm = (form) => {
    const formId = Number(form.id);
    setSelectedFormIds((prev) => {
      const next = new Set(prev);
      if (next.has(formId)) {
        next.delete(formId);
      } else {
        next.add(formId);
      }
      return next;
    });
    setSelectedFormsData((prev) => {
      const next = new Map(prev);
      if (next.has(formId)) {
        next.delete(formId);
      } else {
        next.set(formId, form.name);
      }
      return next;
    });
  };

  const handleRemoveForm = (formId) => {
    const id = Number(formId);
    setSelectedFormIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedFormsData((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const handleSelectTask = (task) => {
    const taskId = Number(task.id);
    applyTasksSelection(new Map([[taskId, task.name]]), !selectedTasks.has(taskId));
  };

  const handleRemoveTask = (taskId) => applyTasksSelection(new Map([[Number(taskId), null]]), false);

  const handleSelectAll = async () => {
    const shouldSelect = !isAllVisibleItemsSelected;
    const visibleFolderIds = isSearchMode ? [] : folders.map((folder) => folder.id);
    const formsMap = new Map(displayForms.map((form) => [Number(form.id), form.name]));
    const tasksMap = new Map(taskRows.map((task) => [Number(task.id), task.name]));

    if (visibleFolderIds.length) {
      setFoldersLoadingState(visibleFolderIds, true);

      try {
        const folderContents = await Promise.all(folders.map((folder) => getFolderContents(folder.id, folder.tree ?? null)));

        folderContents.forEach(({ forms: folderFormsMap, tasks: folderTasksMap }) => {
          folderFormsMap.forEach((name, id) => formsMap.set(id, name));
          folderTasksMap.forEach((name, id) => tasksMap.set(id, name));
        });
      } finally {
        setFoldersLoadingState(visibleFolderIds, false);
      }
    }

    applyFormsSelection(formsMap, shouldSelect);
    applyTasksSelection(tasksMap, shouldSelect);
  };

  const handleConfirm = () => {
    const formsIds = [...selectedFormIds];
    const formsData = [...selectedFormsData.entries()].map(([id, name]) => ({ id, name }));
    const tasksData = [...selectedTasks.entries()].map(([id, name]) => ({ id, name }));
    onConfirmFormsSelection(formsIds, formsData, tasksData);
  };

  const renderBreadcrumbs = () => {
    if (!breadcrumbs.length || isSearchMode) return null;

    return (
      <div className="forms-modal-breadcrumbs">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            {index > 0 && <span className="breadcrumb-separator">{'>'}</span>}
            <button
              className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'breadcrumb-item--active' : ''}`}
              onClick={() => handleBreadcrumbClick(crumb, index)}
              disabled={index === breadcrumbs.length - 1}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderFolders = (folderRows) => {
    if (isSearchMode || !folderRows.length) return null;

    return folderRows.map((folder) => {
      const folderState = folderStates[folder.id];
      const isChecked = folderState === true;
      const isLoadingFolder = loadingFolderIds.has(folder.id);
      const isFolderDisabled = isLoadingFolder || folderState === null;

      return (
        <div key={`folder-${folder.id}`} className="forms-modal-folder-row">
          <label className="form-control-checkbox forms-modal-folder-checkbox" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={isChecked} disabled={isFolderDisabled} onChange={() => handleToggleFolder(folder)} />
            <span></span>
          </label>
          <div className="forms-modal-folder-clickable" onClick={() => handleNavigateToFolder(folder)}>
            <img src="/images/folder-big-icon.png" className={'mr-2'} alt="" />
            <span className="forms-modal-folder-name">{folder.name}</span>
            <svg
              className="forms-modal-folder-chevron"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M7.5 15L12.5 10L7.5 5" stroke="#667085" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      );
    });
  };

  const renderEmptyState = () => {
    if (displayItems.length || folders.length) return null;

    return (
      <div className="no--result" style={{ width: '100%', textAlign: 'center', padding: '24px 27px' }}>
        {isSearchMode ? 'Nothing found for your search.' : 'Nothing to select in this folder.'}
      </div>
    );
  };

  const renderFormsList = (formRows) => {
    return (
      <>
        {formRows.map((form) => {
          const isSelected = selectedFormIds.has(Number(form.id));

          return (
            <div key={`form-${form.id}`} className="forms-modal-form-row">
              <label className="form-control-checkbox forms-modal-form-checkbox" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={isSelected} onChange={() => handleSelectForm(form)} />
                <span></span>
              </label>
              <div className="forms-modal-form-clickable" onClick={() => handleSelectForm(form)}>
                <img src="/images/file-big-icon.png" className={'mr-2'} alt="" />
                <span className="forms-modal-form-name">{form.name}</span>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderTasksList = (rows) =>
    rows.map((task) => {
      const isSelected = selectedTasks.has(Number(task.id));

      return (
        <div key={`task-${task.id}`} className="forms-modal-form-row forms-modal-task-row">
          <label className="form-control-checkbox forms-modal-form-checkbox" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={isSelected} onChange={() => handleSelectTask(task)} />
            <span></span>
          </label>
          <div className="forms-modal-form-clickable" onClick={() => handleSelectTask(task)}>
            <TaskIcon className="forms-modal-task-icon mr-2" />
            <span className="forms-modal-form-name">{task.name}</span>
          </div>
        </div>
      );
    });

  const removeIcon = (onClick) => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={onClick}>
      <path
        d="M12.5003 7.4974L7.50033 12.4974M7.50033 7.4974L12.5003 12.4974M18.3337 9.9974C18.3337 14.5998 14.6027 18.3307 10.0003 18.3307C5.39795 18.3307 1.66699 14.5998 1.66699 9.9974C1.66699 5.39502 5.39795 1.66406 10.0003 1.66406C14.6027 1.66406 18.3337 5.39502 18.3337 9.9974Z"
        stroke="#FDA29B"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const renderSelectedForms = () => {
    const entries = [...selectedFormsData.entries()];
    const taskEntries = [...selectedTasks.entries()];
    return (
      <div className="selected--forms">
        <div className="selected--forms__title mb-1">Selected:</div>
        {!entries?.length && !taskEntries.length && (
          <span className="mt-2" style={{ color: '#98A2B3', fontSize: 14 }}>
            Nothing selected
          </span>
        )}

        <div className="selected--forms__list">
          {taskEntries.map(([id, name]) => (
            <div className="item item--task" key={`task-${id}`}>
              <span className="item__name">
                <TaskIcon className="forms-modal-task-icon" />
                {name}
              </span>
              {removeIcon(() => handleRemoveTask(id))}
            </div>
          ))}
          {entries.map(([id, name]) => (
            <div className="item" key={id}>
              {name}
              {removeIcon(() => handleRemoveForm(id))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen
      className="modal-default pt-4 statistics-forms-modal"
      onRequestClose={onClose}
      ariaHideApp={false}
      style={{ content: { maxWidth: 824, paddingBottom: 108 } }}
    >
      <div className="modal-header-row">
        <h3 className="modal-title">Select forms and tasks</h3>
        <button className="modal-close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div className="modal-body" style={{ marginTop: 0 }}>
        <div className="form-control-item">
          <input
            value={search}
            type="text"
            className="form-control"
            placeholder="Search by name..."
            onChange={(e) => setSearch(e.target.value)}
          />
          <i className="icon-search"></i>
        </div>

        {renderBreadcrumbs()}

        <div className="forms--container">
          <div className="forms--suggest">
            {isLoading ? (
              <div className="no--result" style={{ width: '100%', textAlign: 'center', padding: '24px 27px' }}>
                Loading...
              </div>
            ) : (
              <div className="forms--suggest__list">
                <div className="form-control-checkbox select-all-checkbox">
                  <input
                    name="select--all"
                    type="checkbox"
                    checked={isAllVisibleItemsSelected}
                    disabled={loadingFolderIds.size > 0}
                    onChange={handleSelectAll}
                  />
                  <span>Select All</span>
                </div>
                {renderFolders(libraryFolders)}
                {renderFormsList(libraryItems)}

                {hasTasksItems && (
                  <>
                    {/* The line the Tasks page starts at: everything below it is
                        picked from that tree, not the library folders above. */}
                    <div className="forms-modal-tasks-separator">
                      <TaskIcon />
                      Tasks
                    </div>
                    {renderFolders(tasksFolders)}
                    {renderFormsList(tasksItems)}
                    {renderTasksList(taskRows)}
                  </>
                )}

                {renderEmptyState()}
              </div>
            )}
          </div>
          {renderSelectedForms()}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button className="btn d-flex" onClick={handleConfirm}>
          Select
        </button>
      </div>
    </Modal>
  );
};

export default memo(FormsModal);
